import React, { useEffect, useState } from "react";
import { Users, UserPlus, X, Copy, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { InviteButton } from "@/components/InvitePartner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LEVEL_COLOR = {
  L1: "bg-emerald-900 text-white",
  L2: "bg-emerald-800 text-white",
  L3: "bg-emerald-700 text-white",
  L4: "bg-amber-700 text-white",
  L5: "bg-amber-600 text-white",
  L6: "bg-amber-500 text-white",
  L7: "bg-stone-700 text-white",
  L8: "bg-stone-600 text-white",
  L9: "bg-stone-500 text-white",
  L10: "bg-stone-900 text-amber-400",
};

function AddAgentModal({ onClose, onCreated, labelP }) {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (form.mobile.replace(/\D/g, "").length !== 10) return toast.error("Mobile must be 10 digits");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setSubmitting(true);
    try {
      const { data } = await api.post("/team/add-agent", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        mobile: form.mobile.replace(/\D/g, ""),
        password: form.password,
      });
      setCreated({ ...data, plainPassword: form.password });
      onCreated && onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add agent");
    } finally { setSubmitting(false); }
  };

  const copyCreds = () => {
    const text = `Hi ${created.name}, welcome to Crece!\n\nLogin URL: https://crece.in/auth\nEmail: ${created.email}\nPassword: ${created.plainPassword}\n\nPlease change your password after first login. Your referral code: ${created.referral_code}`;
    navigator.clipboard.writeText(text);
    toast.success("Credentials copied");
  };

  const whatsapp = () => {
    const text = `Hi ${created.name}, welcome to Crece!\n\nLogin: https://crece.in/auth\nEmail: ${created.email}\nPassword: ${created.plainPassword}\n\nPlease change your password after first login. Your referral code: *${created.referral_code}*`;
    window.open(`https://wa.me/91${created.mobile}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-7 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="add-agent-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">Add directly</div>
            <h3 className="font-display text-2xl font-black mt-1">{created ? `${labelP} created` : `Add new ${labelP}`}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded-full" data-testid="add-agent-close"><X className="w-5 h-5 text-stone-500" /></button>
        </div>

        {!created ? (
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <Label className="font-semibold text-sm">Full name</Label>
              <Input data-testid="add-agent-name" value={form.name} required
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Riya Sharma" className="mt-1.5 h-11 rounded-lg" />
            </div>
            <div>
              <Label className="font-semibold text-sm">Email (their username)</Label>
              <Input data-testid="add-agent-email" type="email" value={form.email} required
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com" className="mt-1.5 h-11 rounded-lg" />
            </div>
            <div>
              <Label className="font-semibold text-sm">Mobile (for WhatsApp)</Label>
              <Input data-testid="add-agent-mobile" value={form.mobile} required maxLength={10} inputMode="numeric"
                onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                placeholder="9876543210" className="mt-1.5 h-11 rounded-lg font-mono" />
            </div>
            <div>
              <Label className="font-semibold text-sm">Initial password</Label>
              <Input data-testid="add-agent-password" type="text" value={form.password} required
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 8 chars · 1 letter + 1 digit" className="mt-1.5 h-11 rounded-lg font-mono" />
              <p className="text-xs text-stone-500 mt-1">Share this with the {labelP.toLowerCase()} — they can change it from Forgot Password later.</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
              The new {labelP.toLowerCase()} joins as your <b>direct downline</b>. They&apos;ll need to complete KYC + bank details before they can withdraw earnings.
            </div>
            <Button type="submit" disabled={submitting} data-testid="add-agent-submit"
              className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
              {submitting ? "Creating..." : `Create ${labelP}`}
            </Button>
          </form>
        ) : (
          <div className="mt-5 space-y-4" data-testid="add-agent-success">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-bold text-emerald-900">{created.name} is now in your team</div>
                <p className="text-xs text-emerald-800 mt-1">Share the credentials below — they can use them at <span className="font-mono">crece.in/auth</span>.</p>
              </div>
            </div>
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-2 text-sm" data-testid="created-credentials">
              <div><span className="text-xs font-bold uppercase tracking-wider text-stone-500">Email</span><div className="font-mono mt-0.5">{created.email}</div></div>
              <div><span className="text-xs font-bold uppercase tracking-wider text-stone-500">Password</span><div className="font-mono mt-0.5">{created.plainPassword}</div></div>
              <div><span className="text-xs font-bold uppercase tracking-wider text-stone-500">Referral code</span><div className="font-mono mt-0.5">{created.referral_code}</div></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={copyCreds} variant="outline" className="rounded-full h-11" data-testid="copy-creds-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy
              </Button>
              <Button onClick={whatsapp} className="rounded-full bg-[#25D366] hover:bg-[#1ea952] text-white font-bold h-11" data-testid="whatsapp-creds-btn">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
            </div>
            <p className="text-xs text-stone-500 text-center">Tip: ask them to change the password from &ldquo;Forgot Password&rdquo; after first login.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Team() {
  const [team, setTeam] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const { level_names, percentages, partner_label } = useSettings();
  const labelP = partner_label || "Agent";
  const load = () => api.get("/team").then((r) => setTeam(r.data));
  useEffect(() => { load(); }, []);
  if (!team) return <div className="text-stone-500">Loading...</div>;

  const levels = ["L1", "L2", "L3", "L4", "L5", "L6", "L7", "L8", "L9", "L10"];

  return (
    <div className="space-y-8" data-testid="team-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">10-Grade Career Plan</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-2">My Team</h1>
          <p className="text-stone-600 mt-2">Total team members: <b>{team.total}</b></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setAddOpen(true)} data-testid="open-add-agent"
            className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
            <UserPlus className="w-4 h-4 mr-2" /> Add {labelP}
          </Button>
          <InviteButton />
        </div>
      </div>

      {team.total === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 sm:p-8 flex items-start gap-4" data-testid="team-empty-cta">
          <div className="w-12 h-12 rounded-full bg-amber-500 text-white grid place-items-center shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-xl font-black">Build your first team</h3>
            <p className="text-sm text-stone-700 mt-1">Add an {labelP.toLowerCase()} directly with their email + password, or send a referral link. You earn commission whenever they refer products.</p>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => setAddOpen(true)} className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
                <UserPlus className="w-4 h-4 mr-2" /> Add {labelP}
              </Button>
              <InviteButton />
            </div>
          </div>
        </div>
      )}

      {levels.map((lvl, idx) => {
        const members = team.levels[lvl];
        if (!members.length) return null;
        // Tier labels are intentionally generic — they describe DOWNLINE DISTANCE,
        // not career rank. The career rank of each member is shown in the row.
        const TIER_LABELS = ["Direct downline", "2nd-tier downline", "3rd-tier downline", "4th-tier downline", "5th-tier downline",
                             "6th-tier downline", "7th-tier downline", "8th-tier downline", "9th-tier downline", "10th-tier downline"];
        const tierLabel = TIER_LABELS[idx] || `Tier ${idx + 1}`;
        const rankNameFor = (rIdx) => {
          const i = Number(rIdx) || 0;
          if (i === 0) return labelP;
          return (level_names && level_names[i - 1]) || `Rank ${i}`;
        };
        return (
          <div key={lvl} className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid={`team-level-${lvl}`}>
            <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${LEVEL_COLOR[lvl]}`}>{lvl}</span>
                <h3 className="font-display font-black text-lg">{tierLabel}</h3>
                <span className="text-xs text-stone-500">{members.length} member{members.length === 1 ? "" : "s"}</span>
              </div>
              <span className="text-xs font-bold text-amber-700">{percentages?.[idx]}% commission to you</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-t border-stone-100 first:border-0" data-testid={`team-member-${m.id}`}>
                    <td className="py-3 px-6">
                      <div className="font-bold">{m.name}</div>
                      <div className="text-xs text-stone-500 font-mono">+91 {m.mobile}</div>
                    </td>
                    <td className="py-3 px-6">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${(m.rank_index || 0) === 0 ? "bg-stone-100 text-stone-700" : "bg-emerald-50 text-emerald-900 border border-emerald-200"}`}
                            data-testid={`team-member-rank-${m.id}`}>
                        {rankNameFor(m.rank_index)}
                      </span>
                    </td>
                    <td className="py-3 px-6 text-xs text-stone-500 font-mono">{m.referral_code}</td>
                    <td className="py-3 px-6 text-right font-mono font-bold text-emerald-900">{formatINR(m.total_earned)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {addOpen && <AddAgentModal labelP={labelP} onClose={() => setAddOpen(false)} onCreated={load} />}
    </div>
  );
}
